import { z } from 'zod';

import type { Locale } from '@/app/lib/forms/types/form.types';
import {
  BASE_FORM_ERROR_MESSAGE,
  EMAIL_ERROR_MESSAGE,
  FULLNAME_ERROR_MESSAGE,
  getErrorMessage,
  LINE_ID_ERROR_MESSAGE,
  LOCATION_ERROR_MESSAGE,
  MOBILE_ERROR_MESSAGE,
  PRIVACY_ERROR_MESSAGE,
} from '@/app/lib/forms/utils/messages';
import {
  REGEX_FULLNAME_ZOD,
  REGEX_LINE_ID,
  REGEX_TEL_HOME_MOBILE,
} from '@/app/utils/regex';

export const createFullNameSchema = (locale: Locale = 'th') =>
  z
    .string({
      error: getErrorMessage(BASE_FORM_ERROR_MESSAGE, locale),
    })
    .trim()
    .min(1, getErrorMessage(BASE_FORM_ERROR_MESSAGE, locale))
    .max(50, getErrorMessage(FULLNAME_ERROR_MESSAGE.MAX_LENGTH, locale))
    .transform((value) => value.replace(/\s+/g, ' '))
    .refine(
      (value) => {
        if (value.includes(' ')) {
          const parts = value.split(' ');
          const firstName = parts[0];

          return firstName.length >= 2;
        }

        return value.length >= 2;
      },
      {
        message: getErrorMessage(FULLNAME_ERROR_MESSAGE.MIN_LENGTH, locale),
      }
    )
    .refine((value) => REGEX_FULLNAME_ZOD.test(value), {
      message: getErrorMessage(FULLNAME_ERROR_MESSAGE.INVALID_FORMAT, locale),
    });

export const createMobileSchema = (locale: Locale = 'th') =>
  z
    .string({
      error: getErrorMessage(MOBILE_ERROR_MESSAGE.REQUIRED, locale),
    })
    .min(1, getErrorMessage(MOBILE_ERROR_MESSAGE.REQUIRED, locale))
    .refine((value) => REGEX_TEL_HOME_MOBILE.test(value), {
      message: getErrorMessage(MOBILE_ERROR_MESSAGE.INVALID_FORMAT, locale),
    });

export const createEmailSchema = (locale: Locale = 'th') =>
  z
    .string()
    .email(getErrorMessage(EMAIL_ERROR_MESSAGE.INVALID_FORMAT, locale))
    .nullable()
    .optional()
    .or(z.literal(''));

export const createProvinceSchema = (locale: Locale = 'th') =>
  z
    .string({
      error: getErrorMessage(LOCATION_ERROR_MESSAGE.PROVINCE_REQUIRED, locale),
    })
    .min(1, getErrorMessage(LOCATION_ERROR_MESSAGE.PROVINCE_REQUIRED, locale));

export const createDistrictSchema = () => z.string().optional();

export const createDealerSchema = (locale: Locale = 'th') =>
  z
    .string({
      error: getErrorMessage(LOCATION_ERROR_MESSAGE.DEALER_REQUIRED, locale),
    })
    .min(1, getErrorMessage(LOCATION_ERROR_MESSAGE.DEALER_REQUIRED, locale));

export const createLineIdSchema = (locale: Locale = 'th') =>
  z
    .string()
    .nullable()
    .optional()
    .refine((val) => !val || val.length <= 20, {
      message: getErrorMessage(LINE_ID_ERROR_MESSAGE.MAX_LENGTH, locale),
    })
    .refine((val) => !val || REGEX_LINE_ID.test(val), {
      message: getErrorMessage(LINE_ID_ERROR_MESSAGE.INVALID, locale),
    })
    .transform((val) => val || undefined);

export const createAvailableTimeSchema = (locale: Locale = 'th') =>
  z
    .string({
      error: getErrorMessage(BASE_FORM_ERROR_MESSAGE, locale),
    })
    .min(1, getErrorMessage(BASE_FORM_ERROR_MESSAGE, locale));

export const createPrivacySchema = (locale: Locale = 'th') =>
  z.boolean().refine((val) => val === true, {
    message: getErrorMessage(PRIVACY_ERROR_MESSAGE.REQUIRED, locale),
  });

export const createMobileAsLineOptionalSchema = () => z.boolean().optional();

export type FullName = z.infer<ReturnType<typeof createFullNameSchema>>;
export type Mobile = z.infer<ReturnType<typeof createMobileSchema>>;
export type PhoneNumber = z.infer<ReturnType<typeof createMobileSchema>>;
export type Email = z.infer<ReturnType<typeof createEmailSchema>>;
export type Province = z.infer<ReturnType<typeof createProvinceSchema>>;
export type District = z.infer<ReturnType<typeof createDistrictSchema>>;
export type Dealer = z.infer<ReturnType<typeof createDealerSchema>>;
